import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useDailyNotes } from '@/hooks/useDailyNotes';
import { useIsMobile, useIsTablet } from '@/hooks/use-mobile';
import { Loader2, Save, Trash2 } from 'lucide-react';
import { useCreateBlockNote } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import '@blocknote/mantine/style.css';
import { useTheme } from '@/contexts/ThemeProvider';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface DayNotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: Date;
}

const DayNotesModal = ({ isOpen, onClose, date }: DayNotesModalProps) => {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const { note, saveNote, deleteNote, isSaving, isDeleting, isLoading } = useDailyNotes(date);
  const [initialContent, setInitialContent] = useState('');
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const { user, currentOrganization } = useAuth();
  
  const editor = useCreateBlockNote({
    uploadFile: async (file: File) => {
      if (!user || !currentOrganization) throw new Error('Not authenticated');
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('note-images')
        .upload(fileName, file);
      
      if (uploadError) throw uploadError;
      
      const { data } = supabase.storage
        .from('note-images')
        .getPublicUrl(fileName);
      
      return data.publicUrl;
    },
  });

  useEffect(() => {
    const loadContent = async () => {
      if (!editor) return;
      
      try {
        if (note?.content && note.content !== initialContent) {
          const blocks = await editor.tryParseMarkdownToBlocks(note.content);
          if (blocks.length > 0) {
            editor.replaceBlocks(editor.document, blocks);
          } else {
            editor.removeBlocks(editor.document);
          }
          setInitialContent(note.content);
        } else if (!note?.content && initialContent) {
          editor.removeBlocks(editor.document);
          setInitialContent('');
        }
      } catch (error) {
        console.error('Error loading notes content:', error);
      }
    };
    loadContent();
  }, [note?.content, editor, initialContent]);

  const handleSave = async () => {
    if (!editor) return;
    const markdown = await editor.blocksToMarkdownLossy(editor.document);
    saveNote(markdown);
  };

  const handleDelete = () => {
    deleteNote();
    if (editor) {
      editor.removeBlocks(editor.document);
    }
  };
  const hasContent = note?.content && note.content.trim().length > 0;
  const useSheet = isMobile || isTablet;
  const englishDayName = format(date, 'EEEE');
  const dayName = t(`calendar.dayNames.${englishDayName}`);

  const renderContent = () => (
    <div className="space-y-4">
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : (
        <div className={useSheet ? "min-h-[50vh] border rounded-md overflow-hidden" : "min-h-[40vh] border rounded-md overflow-hidden"}>
          <BlockNoteView 
            editor={editor} 
            theme={theme === 'dark' ? 'dark' : 'light'}
          />
        </div>
      )}
      
      <div className="flex justify-between gap-2">
        <div>
          {note && hasContent && (
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {t('common.delete')}
            </Button>
          )}
        </div>
        
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {t('common.save')}
          </Button>
        </div>
      </div>
    </div>
  );

  if (useSheet) {
    return (
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent side="bottom" className="h-[90vh] flex flex-col">
          <SheetHeader>
            <SheetTitle>
              {t('calendar.dayNotesTitle', { day: dayName })}
            </SheetTitle>
          </SheetHeader>
          {renderContent()}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('calendar.dayNotesTitle', { day: dayName })}
          </DialogTitle>
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default DayNotesModal;